/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from 'react';

import { ResponsiveLine, Serie } from '@nivo/line';
import { Box, Flex, Spacer } from '@tapresearch/ui/components';

import { Campaign } from '../../../../../api';
import {
  CHART_CONTROL_TYPES,
  CustomControlLegend,
} from './CustomControlLegend';
import { getChartWaveLabels } from './getChartWaveLabels';
import { LineChartLegend } from './LineChartLegend';
import { LineChartSliceTooltip } from './LineChartSliceTooltip';
import { ResponsiveRatingLineChartSwitchProps } from './ResponsiveRatingLineChart';
import { useColorScheme } from './useColorScheme';
import { getLineChartTickValues } from './getLineChartTickValues';

export interface ResponsiveLineChartProps {
  data: Serie[];
  waves: Campaign[];
  legendDirection?: 'row' | 'column';
  customLegendControlProps?: ResponsiveRatingLineChartSwitchProps;
  customLegendType?: keyof typeof CHART_CONTROL_TYPES;
  usePercentage?: boolean;
}

export const ResponsiveLineChart = ({
  data,
  waves,
  legendDirection = 'column',
  customLegendControlProps,
  customLegendType,
  usePercentage,
  ...rest
}: ResponsiveLineChartProps) => {
  const [visiblePoints, setVisiblePoints] = useState([]);
  const chartColors = useColorScheme();
  const chartWaveLabels = getChartWaveLabels(waves);

  const tickValues = useMemo(() => {
    return getLineChartTickValues(data);
  }, [data]);
  const maxValue = Math.max(...tickValues);
  const upperLimit = usePercentage ? maxValue : maxValue + maxValue / 5;
  const dataColors = useMemo(() => {
    return [...chartColors].map((pointColor, i) => {
      return !!visiblePoints.length && !visiblePoints.includes(i)
        ? 'transparent'
        : pointColor;
    });
  }, [chartColors, visiblePoints]);

  const handleLegendClick = useCallback(
    (answerIndex) => {
      const numVisible = visiblePoints.length;
      if (visiblePoints.includes(answerIndex)) {
        setVisiblePoints(
          numVisible === 1
            ? []
            : [...visiblePoints].filter((idx) => idx !== answerIndex)
        );
      } else {
        setVisiblePoints(
          numVisible + 1 === data.length ? [] : [...visiblePoints, answerIndex]
        );
      }
    },
    [data, visiblePoints]
  );

  const waveNumberByLaunchDateMap = useMemo(
    () =>
      waves.reduce<Record<string, number>>((acc, wave) => {
        return {
          ...acc,
          [chartWaveLabels[wave.attributes.providable_id]]:
            wave.attributes.wave_number,
        };
      }, {}),
    [waves, chartWaveLabels]
  );

  const xAxisTicks = useMemo(
    () => [...data[0].data.map((wave) => wave.x)],
    [data]
  );

  return (
    <Box data-testid="responsive-line-chart" bg="white" w="100%">
      <Box height="400px">
        <ResponsiveLine
          data={data}
          margin={{ top: 50, right: 50, bottom: 50, left: 80 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: upperLimit,
            stacked: false,
            reverse: false,
          }}
          useMesh
          enableSlices="x"
          animate
          colors={[...(dataColors as any)]}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickValues: xAxisTicks,
          }}
          axisLeft={{
            tickSize: 10,
            tickPadding: 5,
            tickRotation: 0,
            legend: usePercentage ? 'Responses %' : 'Responses',
            legendOffset: -50,
            legendPosition: 'middle',
            tickValues: usePercentage ? tickValues : 'auto',
          }}
          pointSize={16}
          pointLabelYOffset={0}
          pointColor={{ from: 'color' }}
          pointSymbol={CustomPoint}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'color' }}
          sliceTooltip={(props) => {
            return (
              <LineChartSliceTooltip
                {...props}
                visiblePoints={visiblePoints}
                usePercentage={usePercentage}
                waveNumber={
                  waveNumberByLaunchDateMap[
                    props.slice.points[0].data.x as string
                  ]
                }
              />
            );
          }}
          {...rest}
        />
      </Box>
      <Flex>
        <Box>
          <LineChartLegend
            series={data}
            colors={dataColors}
            visiblePoints={visiblePoints}
            onItemClick={handleLegendClick}
            direction={legendDirection}
          />
        </Box>
        <Spacer />
        <CustomControlLegend
          type={customLegendType}
          controlProps={customLegendControlProps}
        />
      </Flex>
    </Box>
  );
};

interface CustomPointProps {
  size: number;
  color: string;
  borderWidth: number;
  borderColor: string;
}
const CustomPoint = ({
  size,
  color,
  borderWidth,
  borderColor,
}: CustomPointProps) => (
  <g>
    <circle
      fill={color === 'transparent' ? 'transparent' : '#FFF'}
      r={size / 2}
      strokeWidth={borderWidth}
      stroke={borderColor}
    />
    <circle
      r={size / 4}
      strokeWidth={borderWidth}
      stroke={borderColor}
      fill={color}
      fillOpacity={1}
    />
  </g>
);
